import { format } from "date-fns";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Table } from "@/components/ui/table";
import { STATUS_BADGE_STYLES } from "@/features-root-user/organization/constants/status-badges";
import type { FC } from "react";
import { type Organization } from "@/features-root-user/service/organization";

const columns: ColumnDef<Organization>[] = [
  {
    accessorKey: "name",
    header: "Organization",
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status;
      return (
        <Badge variant="secondary" className={STATUS_BADGE_STYLES[status]}>
          {status}
        </Badge>
      );
    },
  },
  {
    accessorKey: "responsiblePerson",
    header: "Responsible Person",
  },
  {
    accessorKey: "createdAt",
    header: "Created",
    cell: ({ row }) => format(new Date(row.original.createdAt), "MMM dd, yyyy"),
  },
];

interface OrganizationTableProps {
  readonly data: readonly Organization[];
}

/**
 * Displays a simple table of Organization data in a pure functional style.
 */
export const OrganizationTable: FC<OrganizationTableProps> = ({ data }) => {
  const table = useReactTable({
    data: [...data], // spread to avoid mutation
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="border-border rounded-lg border">
      <Table className="w-full table-fixed">
        <thead className="border-border bg-muted/50 border-b">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className="text-muted-foreground px-4 py-3 text-left text-xs font-medium"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="divide-border divide-y">
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="bg-card hover:bg-muted/50 transition-colors"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 text-left text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length}
                className="text-muted-foreground h-24 text-center text-sm"
              >
                No results.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};
